import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { VACCINATION_DATA, getAllScheduleWeeks } from "../data/vaxSchedules";
import { relativeTime, parseCutoffToMinutes, toSleepDayKey } from "../shared/lib/date";
import type { ActiveSleepSession, AiResponse, BabyProfile, DailyStats, DevelopmentalLeapResult, DevelopmentalNorm, InsightSnapshot, LogEvent, Task, UnitSystem, UserSettings } from "../shared/types/domain";
import { TYPE_EMOJI, displayVolume, payloadSummary, logTypeLabel, formatDurationMin, buildSleepData } from "../shared/lib/format";
import { CoPilotCard } from "../components/CoPilotCard";
import { predictNextSleep, selectNextSleepEvent, type NextSleepEvent } from "../shared/lib/sleepPrediction";

type View = "home" | "logging" | "analytics" | "settings" | "milestones" | "import" | "tasks";

const ANON_NUDGE_DELAY_MS = 3 * 60 * 1000;

interface HomeViewProps {
  activeBaby: BabyProfile;
  logs: LogEvent[];
  logsLoaded: boolean;
  norm: DevelopmentalNorm | null;
  tasks: Task[];
  userSettings: UserSettings;
  insight: InsightSnapshot | null;
  todaysStats: DailyStats;
  vaccineLogs: LogEvent[];
  ageWeeks: number;
  latestWeightKg: number | null;
  effectiveFeedingTargetMl: number;
  effectiveFeedingMinMl: number;
  growthVelocity: number | null;
  aiResponse: AiResponse | null;
  copilotLoading: boolean;
  copilotGeneratedAt?: string | null;
  hasSufficientData: boolean;
  aiEnabled: boolean;
  upcomingLeap: DevelopmentalLeapResult;
  setView: (v: View) => void;
  setSelectedLog: (log: LogEvent) => void;
  setReferenceModal: (modal: "feeding" | "diapers" | "growth" | "sleepPrediction" | null) => void;
  isAnonymous: boolean;
  onRequestRegister: () => void;
  activeSleepSession: ActiveSleepSession | null;
  signedInAt: string | null;
  onAddLog: () => void;
}

export function HomeView({
  activeBaby,
  logs,
  logsLoaded,
  norm,
  tasks,
  userSettings,
  insight,
  todaysStats,
  vaccineLogs,
  ageWeeks,
  latestWeightKg,
  effectiveFeedingTargetMl,
  aiResponse,
  copilotLoading,
  copilotGeneratedAt,
  hasSufficientData,
  aiEnabled,
  upcomingLeap,
  setView,
  setSelectedLog,
  setReferenceModal,
  isAnonymous,
  onRequestRegister,
  activeSleepSession,
  signedInAt,
  onAddLog,
}: HomeViewProps) {
  const { t } = useTranslation();
  const unitSystem: UnitSystem = userSettings.unitSystem ?? "metric";
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [nudgeReady, setNudgeReady] = useState(
    () => !signedInAt || Date.now() - new Date(signedInAt).getTime() >= ANON_NUDGE_DELAY_MS
  );

  useEffect(() => {
    if (nudgeReady || !signedInAt) return;
    const remaining = ANON_NUDGE_DELAY_MS - (Date.now() - new Date(signedInAt).getTime());
    if (remaining <= 0) { setNudgeReady(true); return; }
    const timer = setTimeout(() => setNudgeReady(true), remaining);
    return () => clearTimeout(timer);
  }, [signedInAt, nudgeReady]);

  const feedingMode = activeBaby.feedingMode ?? "bottle";
  const showBottle = feedingMode === "bottle" || feedingMode === "both";
  const showNursing = feedingMode === "nursing" || feedingMode === "both";
  const feedTarget = effectiveFeedingTargetMl;
  const feedPct = feedTarget > 0 ? Math.min(100, (todaysStats.feedTotalMl / feedTarget) * 100) : 0;
  const feedHue = Math.min(120, feedPct * 1.2);
  const diaperTotal = todaysStats.diaperCountWet + todaysStats.diaperCountSoiled;
  const diaperMin = norm?.diaperMin ?? 0;
  const diaperPct = diaperMin > 0 ? Math.min(100, (diaperTotal / diaperMin) * 100) : 0;
  const diaperHue = Math.min(120, diaperPct * 1.2);
  const sleepCutoffMinutes = parseCutoffToMinutes(userSettings.sleepDayCutoff);
  const todaySleepDayKey = toSleepDayKey(new Date().toISOString(), sleepCutoffMinutes);
  const todaysSleepDurationMin = useMemo(
    () => buildSleepData(logs, [todaySleepDayKey], sleepCutoffMinutes)[0]?.sleepDurationMin ?? 0,
    [logs, todaySleepDayKey, sleepCutoffMinutes]
  );

  const lastFeedLog = logs.find(l => l.type === "feed");
  const feedLogs = logs.filter(l => l.type === "feed").slice(0, 4);
  const avgIntervalMs = feedLogs.length >= 2
    ? feedLogs.slice(0, -1).reduce((sum, _l, i) =>
        sum + (new Date(feedLogs[i].occurredAt).getTime() - new Date(feedLogs[i + 1].occurredAt).getTime()), 0
      ) / (feedLogs.length - 1)
    : null;
  const nextFeedAt = lastFeedLog && avgIntervalMs
    ? new Date(new Date(lastFeedLog.occurredAt).getTime() + avgIntervalMs)
    : null;

  const redFlags = insight?.redFlags ?? [];
  const now = Date.now();
  const sleepPrediction = predictNextSleep(logs, ageWeeks, now, activeSleepSession, sleepCutoffMinutes);
  const nextSleepEvent = sleepPrediction ? selectNextSleepEvent(sleepPrediction) : null;
  const formatSleepWindow = (window: NextSleepEvent["window"]) =>
    t("dashboard.sleepWindowApprox", {
      time: new Date(window.windowStartMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    });

  const dueVaccines = (() => {
    if (!activeBaby.countryCode || !VACCINATION_DATA[activeBaby.countryCode]) return [];
    return getAllScheduleWeeks(activeBaby.countryCode)
      .filter(w => w <= ageWeeks)
      .flatMap(w => (VACCINATION_DATA[activeBaby.countryCode!].schedule[w] ?? []).map(label => ({ week: w, label })))
      .filter(({ week, label }) => !vaccineLogs.find(l =>
        (l.payload.vaccineIds ?? []).includes(label) && l.payload.vaccineWeek === week
      ));
  })();

  const sortedTasks = [...tasks].sort((a, b) => new Date(a.nextDueAt).getTime() - new Date(b.nextDueAt).getTime());
  const overdueTasks = sortedTasks.filter(task => new Date(task.nextDueAt).getTime() < now);
  const dueSoonTasks = sortedTasks.filter(task => {
    const due = new Date(task.nextDueAt).getTime();
    return due >= now && due <= now + 3600000;
  });

  const hasAlerts = redFlags.length > 0 || dueVaccines.length > 0 || overdueTasks.length > 0 || dueSoonTasks.length > 0;
  const isNewProfile = logsLoaded && logs.length === 0;

  return (
    <section className="view dashboard-view" aria-label={t("nav.home")}>

      {isAnonymous && !nudgeDismissed && nudgeReady && !isNewProfile && (
        <div className="anon-nudge-banner" role="status">
          <p className="anon-nudge-text">{t("dashboard.anonNudgeText")}</p>
          <div className="anon-nudge-actions">
            <button type="button" className="anon-nudge-cta" onClick={onRequestRegister}>
              {t("settings.createAccountCta")}
            </button>
            <button
              type="button"
              className="anon-nudge-dismiss"
              aria-label={t("common.close")}
              onClick={() => setNudgeDismissed(true)}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      )}

      {isNewProfile && (
        <div className="dash-empty-state">
          <h3 className="dash-empty-state-title">{t("dashboard.emptyStateTitle")}</h3>
          <button
            type="button"
            className="dash-empty-state-cta"
            aria-label={t("dashboard.emptyStateCta")}
            onClick={onAddLog}
          >
            +
          </button>
        </div>
      )}

      {!isNewProfile && (
      <>
      <div className="stat-grid">
        {showBottle ? (
          <button className="stat-chip" onClick={() => setReferenceModal("feeding")}>
            <span className="stat-chip-label">🍼 {feedingMode === "both" ? t("dashboard.bottleLabel") : t("dashboard.feedingLabel")}</span>
            <span className="stat-chip-value">{displayVolume(todaysStats.feedTotalMl, unitSystem)}</span>
            {latestWeightKg === null ? (
              <span className="stat-chip-sub stat-chip-sub--nudge">{t("dashboard.logWeightShort")}</span>
            ) : (
              <>
                <span className="stat-chip-sub">{t("dashboard.ofGoal", { target: displayVolume(feedTarget, unitSystem) })}</span>
                {showNursing && <span className="stat-chip-sub">🤱 {t("dashboard.sessions", { count: todaysStats.nursingCount ?? 0 })}</span>}
                <div className="stat-chip-bar">
                  <div className="stat-chip-fill" style={{ width: `${feedPct}%`, background: `hsl(${feedHue}, 80%, 42%)` }} />
                </div>
              </>
            )}
          </button>
        ) : (
          <button className="stat-chip" onClick={() => setReferenceModal("feeding")}>
            <span className="stat-chip-label">🤱 {t("dashboard.nursingLabel")}</span>
            <span className="stat-chip-value">{todaysStats.nursingCount ?? 0}</span>
            <span className="stat-chip-sub">{t("dashboard.sessions", { count: todaysStats.nursingCount ?? 0 })}</span>
          </button>
        )}

        <button className="stat-chip" onClick={() => setReferenceModal("diapers")}>
          <span className="stat-chip-label">💩 {t("dashboard.diapers")}</span>
          <span className="stat-chip-value">{diaperTotal}</span>
          <span className="stat-chip-sub">{diaperMin > 0 ? t("dashboard.ofGoal", { target: String(diaperMin) }) : t("dashboard.changesLabel")}</span>
          {diaperMin > 0 && (
            <div className="stat-chip-bar">
              <div className="stat-chip-fill" style={{ width: `${diaperPct}%`, background: `hsl(${diaperHue}, 80%, 42%)` }} />
            </div>
          )}
        </button>

        <div className="stat-chip">
          <span className="stat-chip-label">😴 {t("dashboard.sleep")}</span>
          <span className="stat-chip-value">{todaysStats.sleepCount}</span>
          <span className="stat-chip-sub">
            {todaysSleepDurationMin > 0
              ? `${t("dashboard.naps")} · ${formatDurationMin(todaysSleepDurationMin)}`
              : t("dashboard.naps")}
          </span>
        </div>
      </div>

      {copilotLoading && (
        <div className="dash-card copilot-card--loading" aria-hidden="true">
          <span className="dash-card-label">{t("dashboard.dailyExpectHeader")}</span>
          <div className="copilot-pulse" />
        </div>
      )}

      {!copilotLoading && aiResponse?.outlook && (
        <div className="dash-card">
          <span className="dash-card-label">{t("dashboard.dailyExpectHeader")}</span>
          {aiResponse.outlook.feedingMl != null && showBottle && (
            <div className="feed-timing-row">
              <span className="feed-timing-label">🍼 {t("dashboard.feedingLabel")}</span>
              <span className="feed-timing-value">~{displayVolume(aiResponse.outlook.feedingMl, unitSystem)}</span>
            </div>
          )}
          {aiResponse.outlook.diapers != null && (
            <div className="feed-timing-row">
              <span className="feed-timing-label">💩 {t("dashboard.diapers")}</span>
              <span className="feed-timing-value">~{aiResponse.outlook.diapers} {t("dashboard.changesLabel")}</span>
            </div>
          )}
          {aiResponse.outlook.naps != null && (
            <div className="feed-timing-row">
              <span className="feed-timing-label">😴 {t("dashboard.sleep")}</span>
              <span className="feed-timing-value">~{aiResponse.outlook.naps} {t("dashboard.naps")}</span>
            </div>
          )}
          {aiResponse.outlook.behavioralNote.length > 0 && (
            <ul className="dash-headsup-list">
              {aiResponse.outlook.behavioralNote.map((point: string, i: number) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          )}
          <span className="copilot-card-label">✦ AI generated</span>
          <p className="copilot-disclaimer">{t("copilotDisclaimer")}</p>
        </div>
      )}

      {upcomingLeap.daysUntilNextLandmark !== null && upcomingLeap.daysUntilNextLandmark <= 5 && (
        <div className="dash-card leap-banner">
          <div className="leap-banner-header">
            <span className="leap-banner-label">
              🌱 {upcomingLeap.nextLandmarkWeek != null
                ? t(`norms.w${upcomingLeap.nextLandmarkWeek}.eventName`, { defaultValue: upcomingLeap.eventName ?? t("dashboard.upcomingLeapFallback") })
                : (upcomingLeap.eventName ?? t("dashboard.upcomingLeapFallback"))}
            </span>
            <span className="leap-banner-days">
              {upcomingLeap.daysUntilNextLandmark === 0
                ? t("dashboard.upcomingLeapToday")
                : upcomingLeap.daysUntilNextLandmark === 1
                ? t("dashboard.upcomingLeapTomorrow")
                : t("dashboard.upcomingLeapDays", { days: upcomingLeap.daysUntilNextLandmark })}
            </span>
          </div>
          {upcomingLeap.nextLandmarkWeek != null && (
            <p className="dash-headsup">
              {t(`norms.w${upcomingLeap.nextLandmarkWeek}.headsUp`, { defaultValue: upcomingLeap.headsUpPreview ?? "" }).split(".")[0]}
            </p>
          )}
          <button className="nudge-link" onClick={() => setView("milestones")}>
            {t("dashboard.upcomingLeapSeeMore")}
          </button>
        </div>
      )}

      {aiEnabled && !hasSufficientData && (
        <div className="dash-card">
          <p className="dash-headsup">{t("dashboard.dataSparseNudge")}</p>
        </div>
      )}

      {!aiEnabled && (
        <div className="dash-card">
          <p className="dash-headsup">{t("dashboard.aiNudgeText")}</p>
          <button className="nudge-link" onClick={() => setView("settings")}>
            {t("dashboard.aiNudgeButton")}
          </button>
        </div>
      )}

      {lastFeedLog && (
        <div className="dash-card dash-feed-timing">
          <div className="feed-timing-row">
            <span className="feed-timing-label">{t("dashboard.lastFed")}</span>
            <span className="feed-timing-value">{relativeTime(lastFeedLog.occurredAt)}</span>
          </div>
          {nextFeedAt && (
            <div className="feed-timing-row">
              <span className="feed-timing-label">{t("dashboard.nextFeed")}</span>
              <span className="feed-timing-value">~{nextFeedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          )}
        </div>
      )}

      {nextSleepEvent && (
        <div className="dash-card dash-sleep-window">
          <div className="dash-card-header-row">
            <span className="dash-card-label">{t("dashboard.sleepTimeHeader")}</span>
            <button
              type="button"
              className="stat-info-btn"
              aria-label={t("dashboard.sleepTimeInfoLabel")}
              onClick={() => setReferenceModal("sleepPrediction")}
            >
              <span className="stat-info-icon" aria-hidden="true">ℹ</span>
            </button>
          </div>
          <div className="feed-timing-row">
            <span className="feed-timing-label">
              {t(nextSleepEvent.kind === "nap" ? "dashboard.nextNap" : "dashboard.nextBedtime")}
            </span>
            <span className="feed-timing-value">{formatSleepWindow(nextSleepEvent.window)}</span>
          </div>
        </div>
      )}

      {logs.length > 0 && (
        <button className="dash-card" onClick={() => { setView("logging"); setSelectedLog(logs[0]); }}>
          <span className="dash-card-label">{t("dashboard.lastLogged")}</span>
          <div className="dash-last-row">
            <span className="dash-last-icon">{TYPE_EMOJI[logs[0].type]}</span>
            <span className="dash-last-type">{logTypeLabel(logs[0].type)}</span>
            {payloadSummary(logs[0], unitSystem) && (
              <span className="dash-last-payload">{payloadSummary(logs[0], unitSystem)}</span>
            )}
            <span className="dash-last-time">{relativeTime(logs[0].occurredAt)}</span>
          </div>
        </button>
      )}

      <CoPilotCard aiResponse={aiResponse} loading={copilotLoading} ageWeeks={ageWeeks} generatedAt={copilotGeneratedAt} />

      {hasAlerts && (
        <div className="dash-card dash-card--alerts">
          <span className="dash-card-label">⚠ {t("dashboard.alertsHeader")}</span>
          {redFlags.map((f, i) => (
            <p key={i} className="dash-alert-flag">{f}</p>
          ))}
          {dueVaccines.length > 0 && (
            <p className="dash-alert-item">
              {t("dashboard.vaccinesOverdue")}
              {" · "}
              <button className="nudge-link" onClick={() => setView("milestones")}>{t("dashboard.seeVaccinationSchedule")}</button>
            </p>
          )}
          {overdueTasks.length > 0 && (
            <p className="dash-alert-item">
              <strong>{t("dashboard.overdueCount", { count: overdueTasks.length })}</strong>
              {" · "}{overdueTasks[0].title}
            </p>
          )}
          {dueSoonTasks.map(task => (
            <p key={task.id} className="dash-alert-item">{task.title} · {t("dashboard.dueSoon")}</p>
          ))}
          {(overdueTasks.length > 0 || dueSoonTasks.length > 0) && (
            <button className="nudge-link" onClick={() => setView("tasks")}>{t("dashboard.seeAllTasks")}</button>
          )}
        </div>
      )}

      {(insight?.milestonesExpected?.length ?? 0) > 0 && (
        <div className="dash-card">
          <span className="dash-card-label">{t("dashboard.weekMilestones", { week: ageWeeks })}</span>
          <ul className="dash-list">
            {insight!.milestonesExpected.map((m, i) => {
              const translated = t(`norms.w${norm?.ageWeeks}.milestones`, { returnObjects: true });
              const display = Array.isArray(translated) ? (translated[i] ?? m) : m;
              return <li key={i}>{display}</li>;
            })}
          </ul>
        </div>
      )}
      </>
      )}

    </section>
  );
}
